import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "./Navbar";
import "./SingleBlog.css";
import { isAuthenticated } from "../utils/session";
import Comments from "./Comments";
import LikeButton from "./LikeButton";
import BuyTreat from "./Treat/BuyTreat";

function SingleBlog() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog]               = useState(null);
  const [otherBlogs, setOtherBlogs]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(true);
  const [shareToast, setShareToast]   = useState(null);
  const [modalContent, setModalContent] = useState(null);

  // Auth
  const [token]  = useState(localStorage.getItem("token"));
  const userId   = localStorage.getItem("userId");
  const username = localStorage.getItem("username");

  // Engagement counters, keyed by blog id (main card + every "More Blogs" card)
  const [commentCounts, setCommentCounts]   = useState({});
  const [shareCounts, setShareCounts]       = useState({});
  const [saveCounts, setSaveCounts]         = useState({});

  // Save / follow
  const [savedBlogs, setSavedBlogs]         = useState([]);
  const [followStatus, setFollowStatus]     = useState({});
  const [followLoading, setFollowLoading]   = useState({});
  const [authorProfiles, setAuthorProfiles] = useState({});
  const [myProfilePic, setMyProfilePic]     = useState("");

  // Other blogs state
  const [expandedOther, setExpandedOther]     = useState({});
  const [otherShareToast, setOtherShareToast] = useState(null);
  const [otherFollowStatus, setOtherFollowStatus]   = useState({});
  const [otherFollowLoading, setOtherFollowLoading] = useState({});

  // Comment open tracking — shared Comments component handles the rest
  const [openCommentId, setOpenCommentId]           = useState(null);
  const [otherOpenCommentId, setOtherOpenCommentId] = useState(null);

  // Pagination for "More Blogs"
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 10;

  const canvasRef = useRef(null);
  const videoRefs = useRef({});

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  // ── PARTICLE CANVAS ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = Array.from({ length: 60 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r:  Math.random() * 1.5 + 0.5,
    }));
    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx   = particles[i].x - particles[j].x;
          const dy   = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139,92,246,${1 - dist / 100})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.arc(particles[i].x, particles[i].y, particles[i].r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(167,139,250,0.6)";
        ctx.fill();
        particles[i].x += particles[i].vx;
        particles[i].y += particles[i].vy;
        if (particles[i].x < 0 || particles[i].x > canvas.width)  particles[i].vx *= -1;
        if (particles[i].y < 0 || particles[i].y > canvas.height) particles[i].vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);

  // ── AUTH GUARD ──
  useEffect(() => {
    if (!isAuthenticated()) {
      localStorage.setItem("redirectAfterLogin", `/blog/${id}`);
      navigate("/");
      return;
    }
    fetchBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── FETCH MY PROFILE PIC ──
  useEffect(() => {
    if (!username) return;
    axios
      .get(`${BASE_URL}/api/profile/username/${encodeURIComponent(username)}`)
      .then(res => { if (res.data?.profilePic) setMyProfilePic(res.data.profilePic); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // ── SAVED BLOGS ──
  const fetchSavedBlogs = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/blogs/saved`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { _t: Date.now() },
      });
      setSavedBlogs(res.data.savedBlogs.map(b => typeof b === "object" ? b._id : b));
    } catch (err) { console.error(err); }
  }, [BASE_URL, token]);

  useEffect(() => { fetchSavedBlogs(); }, [fetchSavedBlogs]);

  const handleSaveToggle = async (blogId) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.saveCount === "number") {
        setSaveCounts(prev => ({ ...prev, [blogId]: res.data.saveCount }));
      }
      fetchSavedBlogs();
    } catch (err) { console.error(err); }
  };

  const isSaved = (blogId) => savedBlogs.includes(blogId);

  // ── AUTHOR PROFILE ──
  const fetchAuthorProfile = async (authorName) => {
    try {
      if (authorProfiles[authorName]) return;
      const res = await axios.get(`${BASE_URL}/api/profile/username/${authorName}`);
      setAuthorProfiles(prev => ({ ...prev, [authorName]: res.data.profilePic || "" }));
    } catch { /* no profile */ }
  };

  // ── FOLLOW STATUS ──
  // The names of everybody the signed-in reader follows, lowercased for
  // comparison. Blocked users are already excluded by the endpoint. Returns an
  // empty set on failure, so a hiccup here can never widen what is shown.
  const fetchFollowedNames = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/users/${encodeURIComponent(username)}/following`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return new Set(
        (res.data.following || []).map(u => (u.name || "").trim().toLowerCase())
      );
    } catch {
      return new Set();
    }
  };

  const checkFollowStatuses = async (authors, setFn) => {
    const statuses = {};
    await Promise.all(
      authors.map(async author => {
        try {
          const res = await axios.get(`${BASE_URL}/api/users/${author}/follow-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          statuses[author] = res.data.isFollowing;
        } catch { statuses[author] = false; }
      })
    );
    setFn(statuses);
  };

  // ── FOLLOW TOGGLE ──
  const makeFollowToggle = (setStatusFn, setLoadingFn, statusObj, loadingObj) =>
    async (authorName) => {
      if (!authorName || loadingObj[authorName]) return;
      const was = statusObj[authorName];
      setStatusFn(prev => ({ ...prev, [authorName]: !was }));
      setLoadingFn(prev => ({ ...prev, [authorName]: true }));
      try {
        await axios.post(`${BASE_URL}/api/users/${authorName}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        setStatusFn(prev => ({ ...prev, [authorName]: was }));
      } finally {
        setLoadingFn(prev => ({ ...prev, [authorName]: false }));
      }
    };

  const toggleFollow      = makeFollowToggle(setFollowStatus,      setFollowLoading,      followStatus,      followLoading);
  const toggleOtherFollow = makeFollowToggle(setOtherFollowStatus, setOtherFollowLoading, otherFollowStatus, otherFollowLoading);

  // ── FETCH BLOG ──
  // The MAIN blog (the one this page is actually about) is fetched from its
  // own dedicated GET /api/blogs/:id endpoint rather than pulled out of the
  // full /api/blogs list. This is the single most important change: the
  // "More Blogs" list can afford to be a little stale, but the main blog's
  // hideComments/allowShare/allowSave MUST always reflect the latest saved
  // settings, and hitting its own endpoint directly removes every possible
  // source of staleness (list caching, list-building logic, filtering, etc).
  const fetchBlog = async () => {
    try {
      setLoading(true);

      const [mainRes, allRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/blogs/${id}`, { params: { _t: Date.now() } }),
        axios.get(`${BASE_URL}/api/blogs`,       { params: { _t: Date.now() } }),
      ]);

      const current  = mainRes.data;
      const allBlogs  = allRes.data;

      // Seed engagement counters for every card this page can render —
      // the main blog and all of the "More Blogs" entries.
      const comments = {}, shares = {}, saves = {};
      allBlogs.forEach(b => {
        comments[b._id] = b.commentCount ?? 0;
        shares[b._id]   = b.shareCount   ?? 0;
        saves[b._id]    = b.saveCount    ?? 0;
      });
      if (current) {
        comments[current._id] = current.commentCount ?? comments[current._id] ?? 0;
        shares[current._id]   = current.shareCount   ?? shares[current._id]   ?? 0;
        saves[current._id]    = current.saveCount    ?? saves[current._id]    ?? 0;
      }
      setCommentCounts(comments);
      setShareCounts(shares);
      setSaveCounts(saves);

      if (current) {
        setBlog({
          ...current,
          likes:        current.likes    || [],
          dislikes:     current.dislikes || [],
          likeCount:    current.likes?.length    || 0,
          dislikeCount: current.dislikes?.length || 0,
        });
        fetchAuthorProfile(current.author);
        checkFollowStatuses([current.author], setFollowStatus);
      }

      let others = allBlogs
        .filter(b => b._id !== id)
        .map(b => ({
          ...b,
          likes:        b.likes    || [],
          dislikes:     b.dislikes || [],
          likeCount:    b.likes?.length    || 0,
          dislikeCount: b.dislikes?.length || 0,
        }));

      // "More Blogs" follows the same rule as the feed: a signed-in reader
      // only ever sees blogs by the people they follow. A visitor still sees
      // everything, exactly as before. One request for the whole following
      // list — not one per author.
      if (token && username) {
        const followed = await fetchFollowedNames();
        others = others.filter(b =>
          followed.has((b.author || "").trim().toLowerCase())
        );
      }
      setOtherBlogs(others);

      const uniqueAuthors = [...new Set(others.map(b => b.author))];
      uniqueAuthors.forEach(fetchAuthorProfile);
      checkFollowStatuses(uniqueAuthors, setOtherFollowStatus);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ── SHARE ──
  const recordShare = async (blogId) => {
    setShareCounts(prev => ({ ...prev, [blogId]: (prev[blogId] ?? 0) + 1 }));
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/share`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.shareCount === "number") {
        setShareCounts(prev => ({ ...prev, [blogId]: res.data.shareCount }));
      }
    } catch (err) {
      console.error("Share count failed:", err);
      setShareCounts(prev => ({ ...prev, [blogId]: Math.max((prev[blogId] ?? 1) - 1, 0) }));
    }
  };

  const handleShare = async (b, setToastFn) => {
    const shareUrl = `${window.location.origin}/blog/${b._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: b.title, text: `Check out this blog: ${b.title}`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setToastFn(b._id);
        setTimeout(() => setToastFn(null), 2000);
      }
    } catch (err) {
      // Share sheet dismissed or clipboard blocked — nothing was shared.
      console.error(err);
      return;
    }
    recordShare(b._id);
  };

  // ── VIDEO ──
  const handleVideoPlay = (videoId) => {
    Object.keys(videoRefs.current).forEach(key => {
      if (key !== videoId) {
        const v = videoRefs.current[key];
        if (v && !v.paused) v.pause();
      }
    });
  };

  // ── MODAL ──
  const openModal  = (type, url) => setModalContent({ type, url });
  const closeModal = () => setModalContent(null);

  // ── LOADING ──
  if (loading) return (
    <div className="sb-wrapper">
      <canvas ref={canvasRef} className="sb-canvas" />
      <Navbar />
      <div className="sb-loading">
        <div className="sb-spinner" />
        <p>Loading blog...</p>
      </div>
    </div>
  );

  if (!blog) return (
    <div className="sb-wrapper">
      <canvas ref={canvasRef} className="sb-canvas" />
      <Navbar />
      <div className="sb-loading">
        <p style={{ color: "rgba(226,232,240,0.5)" }}>Blog not found.</p>
      </div>
    </div>
  );

  // ── CARD RENDERER ──
  const renderCard = ({
    b,
    isMain,
    isExpandedVal,
    onToggleExpand,
    initialLikes,
    initialLiked,
    onShare,
    shareToastVal,
    isCommentOpen,
    onToggleComment,
    authorPic,
    followStatusMap,
    followLoadingMap,
    onToggleFollow,
    onClick,
  }) => (
    <div
      className={`sb-blog-card${isMain ? " sb-main-card" : ""}`}
      onClick={onClick || undefined}
    >
      {/* AUTHOR */}
      <div className="author-section" onClick={e => onClick && e.stopPropagation()}>
        <img
          src={authorPic || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
          className="author-profile-pic"
          alt=""
        />
        <p>
          <Link to={`/authorprofile/${b.author}`} onClick={e => e.stopPropagation()}>
            {b.author}
          </Link>
        </p>
        {b.author !== username && (
          <button
            className={`follow-btn ${followStatusMap[b.author] ? "following" : ""}`}
            onClick={e => { e.stopPropagation(); onToggleFollow(b.author); }}
            disabled={followLoadingMap[b.author]}
          >
            {followLoadingMap[b.author] ? "..." : followStatusMap[b.author] ? "Following" : "Follow"}
          </button>
        )}
      </div>

      {/* TITLE */}
      <h2>{b.title}</h2>

      {/* CONTENT */}
      <p onClick={e => e.stopPropagation()}>
        {isExpandedVal ? b.content : b.content.slice(0, 150)}
        {b.content.length > 150 && (
          <span
            className="read-more"
            onClick={e => { e.stopPropagation(); onToggleExpand(); }}
          >
            {isExpandedVal ? " Read Less" : " Read More"}
          </span>
        )}
      </p>

      {/* IMAGE */}
      {b.image && (
        <img
          src={b.image}
          className="blog-image"
          alt=""
          onClick={e => { e.stopPropagation(); openModal("image", b.image); }}
        />
      )}

      {/* VIDEO */}
      {b.video && (
        <video
          src={b.video}
          controls
          ref={el => (videoRefs.current[b._id] = el)}
          onPlay={() => handleVideoPlay(b._id)}
          className="blog-video"
          onClick={e => e.stopPropagation()}
        />
      )}
      {/* ACTIONS */}
      <div className="blog-actions" onClick={e => e.stopPropagation()}>
        <div className="actions-left">

          {/* ── LIKE — self-contained LikeButton ── */}
          <LikeButton
            blogId={b._id}
            initialLikes={initialLikes}
            initialLiked={initialLiked}
            token={token}
          />

          {/* ── COMMENT TOGGLE ── */}
          {!b.hideComments && (
            <button
              className={`action-btn comment-btn ${isCommentOpen ? "active" : ""}`}
              onClick={onToggleComment}
              type="button"
            >
              <svg viewBox="0 0 24 24" className="action-icon comment-icon">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>{commentCounts[b._id] ?? 0}</span>
            </button>
          )}

          {/* ── SHARE ── */}
          {b.allowShare !== false && (
            <div className="share-wrapper">
              <button className="action-btn share-btn" onClick={() => onShare()} type="button">
                <svg viewBox="0 0 24 24" className="action-icon share-icon">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                <span>{shareCounts[b._id] ?? 0}</span>
              </button>
              {shareToastVal === b._id && (
                <span className="share-toast">Link copied!</span>
              )}
            </div>
          )}

          {/* ── BUY ME A TREAT — only on posts the author enabled, never on your own ── */}
          {b.treatsEnabled && token && b.author !== username && <BuyTreat blogId={b._id} />}
        </div>

        {/* ── SAVE ── */}
        <div className="actions-right">
          {b.allowSave !== false && (
            <button
              className={`action-btn save-btn ${isSaved(b._id) ? "saved" : ""}`}
              onClick={() => handleSaveToggle(b._id)}
              type="button"
            >
              <svg viewBox="0 0 24 24" className="action-icon save-icon">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              <span>{saveCounts[b._id] ?? 0}</span>
            </button>
          )}
        </div>
      </div>

      {/* ── COMMENTS — shared Comments component ── */}
      {!b.hideComments && (
        <div onClick={e => e.stopPropagation()}>
          <Comments
            blogId={b._id}
            blogAuthor={b.author}
            currentUsername={username}
            currentUserPic={myProfilePic}
            isOpen={isCommentOpen}
          />
        </div>
      )}

      <p className="blog-upload-time">🕒 {new Date(b.createdAt).toLocaleString()}</p>
    </div>
  );

  // ── RENDER ──
  const isLikedMain = blog.likes?.some(l =>
    typeof l === "object" ? l._id === userId : l === userId
  );

  const isOtherLiked = (b) =>
    b.likes?.some(l => typeof l === "object" ? l._id === userId : l === userId);

  return (
    <div className="sb-wrapper">
      <canvas ref={canvasRef} className="sb-canvas" />
      <Navbar />

      <div className="sb-container">

        {/* HEADER */}
        <div className="sb-header">
          <div className="vb-badge">✦ Reading</div>
          <h1 className="vb-title">Blog Post</h1>
          <p className="vb-sub">You're reading a post from our community</p>
        </div>

        {/* MAIN BLOG CARD */}
        {renderCard({
          b:              blog,
          isMain:         true,
          isExpandedVal:  expanded,
          onToggleExpand: () => setExpanded(v => !v),
          initialLikes:   blog.likeCount || 0,
          initialLiked:   isLikedMain,
          onShare:        () => handleShare(blog, setShareToast),
          shareToastVal:  shareToast,
          isCommentOpen:  openCommentId === blog._id,
          onToggleComment: () => setOpenCommentId(prev => prev === blog._id ? null : blog._id),
          authorPic:      authorProfiles[blog.author],
          followStatusMap: followStatus,
          followLoadingMap: followLoading,
          onToggleFollow: toggleFollow,
          onClick:        null,
        })}

        {/* MORE BLOGS */}
        {otherBlogs.length > 0 && (() => {
          const totalPages      = Math.ceil(otherBlogs.length / blogsPerPage);
          const indexOfLast     = currentPage * blogsPerPage;
          const currentOtherBlogs = otherBlogs.slice(indexOfLast - blogsPerPage, indexOfLast);

          return (
            <>
              <div className="sb-more-header">
                <div className="vb-badge">✦ Discover</div>
                <h2 className="sb-more-title">More Blogs</h2>
                <p className="sb-more-count">
                  Showing {indexOfLast - blogsPerPage + 1}–{Math.min(indexOfLast, otherBlogs.length)} of {otherBlogs.length} posts
                </p>
              </div>

              <div className="blogs-list">
                {currentOtherBlogs.map(item =>
                  renderCard({
                    b:              item,
                    isMain:         false,
                    isExpandedVal:  !!expandedOther[item._id],
                    onToggleExpand: () =>
                      setExpandedOther(prev => ({ ...prev, [item._id]: !prev[item._id] })),
                    initialLikes:   item.likeCount || 0,
                    initialLiked:   isOtherLiked(item),
                    onShare:        () => handleShare(item, setOtherShareToast),
                    shareToastVal:  otherShareToast,
                    isCommentOpen:  otherOpenCommentId === item._id,
                    onToggleComment: () =>
                      setOtherOpenCommentId(prev => prev === item._id ? null : item._id),
                    authorPic:      authorProfiles[item.author],
                    followStatusMap: otherFollowStatus,
                    followLoadingMap: otherFollowLoading,
                    onToggleFollow: toggleOtherFollow,
                    onClick:        () => navigate(`/blog/${item._id}`),
                  })
                )}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="page-arrow"
                    onClick={() => { setCurrentPage(p => Math.max(p - 1, 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={currentPage === 1}
                  >‹</button>

                  {Array.from({ length: totalPages }).map((_, i) => {
                    const page = i + 1;
                    const show = page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                    const showEllipsisAfter  = page === currentPage + 2 && currentPage < totalPages - 2;
                    if (showEllipsisBefore || showEllipsisAfter)
                      return <span key={i} className="page-ellipsis">…</span>;
                    if (!show) return null;
                    return (
                      <button
                        key={i}
                        className={currentPage === page ? "page-active" : ""}
                        onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    className="page-arrow"
                    onClick={() => { setCurrentPage(p => Math.min(p + 1, totalPages)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={currentPage === totalPages}
                  >›</button>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* MODAL */}
      {modalContent && (
        <div className="image-modal" onClick={closeModal}>
          <span>×</span>
          <img src={modalContent.url} alt="" />
        </div>
      )}
    </div>
  );
}

export default SingleBlog;